import axios from "axios";
import settings from "../../configs/appSettings";
import {CurrentUser} from "../CurrentUser";
import {PortalUser} from "../PortalUser";
import authService from './AuthenticationService';
import {compareAsc} from "date-fns";

export class HttpService {

    constructor(config) {
        this.config = config;
    }

    static create() {
        return new HttpService({
            baseURL: settings.apiUrl,
            headers: { "Authorization": `Bearer ${CurrentUser.accessToken}`}
        });
    }

    static createWithoutAuthorization() {
        return new HttpService({
            baseURL: settings.apiUrl
        });
    }

    static createForPortal() {
        return new HttpService({
            baseURL: settings.apiUrl,
            headers: { "x-ncontracts-poll-portal-token": PortalUser.token }
        });
    }

    static createForAuthApi() {
        return new HttpService({
            baseURL: settings.authApiUrl,
            headers: { "Authorization": `Bearer ${CurrentUser.accessToken}`}
        });
    }

    requestTransfer(productTarget, additionalRoute) {
        const requestBody = {
            product: productTarget,
            additionalRouteInfo: additionalRoute
        };

        return this.post("/api/transfer", requestBody);
    }

    logoutAndNavigateToLoginPage() {
        CurrentUser.logout();
        authService.checkForAndInformParentOfTimeout(settings.northBaseUrl);
        window.location.replace(`${settings.authUiUrl}?referrer=${settings.clientId}&ReturnUrl=${encodeURIComponent(window.location.href)}`);
    }

    createAxiosInstance() {
        const instance = axios.create();

        // Add timestamp to GET calls so IE won't cache it
        instance.interceptors.request.use(function (request) {
            if (request.method === 'get') {

                const timestamp = new Date().getTime();

                if (request.url.indexOf("?") === -1) {
                    request.url += "?t=" + timestamp;
                } else {
                    request.url += "&t=" + timestamp;
                }
            }

            return request;
        });

        instance.interceptors.response.use(response => {
            return response;
        }, errorResponse => {

            if (errorResponse !== undefined && errorResponse.response !== undefined && errorResponse.response.status === 304) {
                // We're using 304 when a portal user tries to view a poll they've already responded to
                return {data: {httpStatusCode: 304}};
            }

            if (errorResponse === undefined || errorResponse.response === undefined) {
                this.logoutAndNavigateToLoginPage();
                return;
            }

            if (errorResponse.response.status === 500) {
                return;
            }

            if (errorResponse.response.status === 401 || errorResponse.response.status === 400 || errorResponse.response.status === 415) {

                const originalRequest = errorResponse.config;

                try {
                    if (compareAsc(new Date(CurrentUser.tokenExpirationDate), new Date()) === -1){
                        this.logoutAndNavigateToLoginPage();
                        return;
                    }
                } catch (e) {
                    this.logoutAndNavigateToLoginPage();
                    return;
                }

                return new Promise((resolve, reject) => {
                    this.tryRefreshToken()
                        .then(() => {
                            originalRequest.headers.Authorization = `Bearer ${CurrentUser.accessToken}`;
                            resolve(axios(originalRequest));
                        })
                        .catch(() => {
                            this.logoutAndNavigateToLoginPage();
                            reject();
                        });
                });
            } else if (errorResponse.response.status === 404 || errorResponse.response.status === 410 || errorResponse.response.status == 504) {
                //404: Not found
                //410: Gone
                //504: AppGateway Timeout.
                //These are okay.
                return;
            } else {
                this.logoutAndNavigateToLoginPage();
            }
        });

        return instance;
    }

    get(url, query, axiosConfig) {
        return new Promise((resolve, reject) => {

            const q = query !== undefined ? `?${query}` : "";
            const entireUrl = `${url.trim()}${q}`;

            const c = {...this.config, axiosConfig};

            return this.createAxiosInstance()
                        .get(entireUrl,c)
                        .then(result => resolve(result))
                        .catch(error => reject(error));
        });
    }

    post(url, payload) {
        return new Promise((resolve, reject) => {
            return this.createAxiosInstance()
                .post(url, payload, this.config)
                .then(result => resolve(result))
                .catch(error => reject(error));
        });
    }

    put(url, payload) {
        return new Promise((resolve, reject) => {
            return this.createAxiosInstance()
                .put(url, payload, this.config)
                .then(result => resolve(result))
                .catch(error => reject(error));
        });
    }

    patch(url, payload) {
        return new Promise((resolve, reject) => {
            return this.createAxiosInstance()
                .patch(url, payload, this.config)
                .then(result => resolve(result))
                .catch(error => reject(error));
        });
    }

    delete(url) {
        return new Promise((resolve, reject) => {
            return this.createAxiosInstance()
                .delete(url, this.config)
                .then(result => resolve(result))
                .catch(error => reject(error));
        });
    }

    uploadFile(url, fileObject, progressCallback, cancelTokenSource) {
        return new Promise((resolve, reject) => {

            let baseConfig = this.config;
            baseConfig.onUploadProgress = progressCallback;
            baseConfig.cancelToken = cancelTokenSource.token;

            let data = new FormData();
            data.append('file', fileObject.raw);
            data.append('document-info', JSON.stringify(
                fileObject.documentDetails
            ));

            return this.createAxiosInstance()
                        .post(url, data, baseConfig)
                        .then(result => resolve(result))
                        .catch(err => {
                            if (axios.isCancel(err)) {
                                const {message} = err;
                                reject(message === undefined ? "Action canceled." : message);
                            }else {
                                const {message} = err;
                                reject(message === undefined ? "Server error." : message);
                            }
                        });
        });
    }

    createAxiosCancelToken() {
        return axios.CancelToken;
    }

    callApiAnonymously(actionType, url, data) {

        return new Promise((resolve, reject) => {

            return axios({
                        baseURL: settings.apiUrl,
                        method: actionType,
                        url: url,
                        headers: {
                            "Content-Type": "application/json"
                        },
                        data: data === null || data !== undefined
                                ? '{}'
                                : JSON.stringify(data)
                    })
                .then(result => resolve(result))
                .catch(error => reject(error));

        });
    }

    tryRefreshToken() {
        return new Promise((resolve, reject) => {
            const requestBody = `grant_type=refresh_token&client_id=${settings.clientId}&refresh_token=${encodeURIComponent(CurrentUser.refreshToken)}`;

            const header = {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                baseURL: settings.authApiUrl
            };

            return this.createAxiosInstance()
                .post("/login", requestBody, header)
                .then(result => {
                    const {data} = result;
                    CurrentUser.setTokenInfo(
                        data.access_token,
                        data.refresh_token,
                        data.userId,
                        data.refreshTokenExpirationMinutes);

                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    }
}
